import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Grid,
  TextField,
  Link,
  Stack
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import { get } from '../../utility/fetch2';

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    maxWidth: '95%',
    width: '95%',
    margin: theme.spacing(2)
  }
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  padding: theme.spacing(1.5),
  textAlign: 'center',
  '&.MuiTableCell-head': {
    backgroundColor: theme.palette.grey[100],
    fontWeight: 600
  }
}));

function ViewVisit({ closeModal, visit, next }) {
  const [nurses, setNurses] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [payload, setPayload] = useState({});
  const [tiffData, setTiffData] = useState(null);

  useEffect(() => {
    getNurses();
    getDoctors();
  }, []);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setPayload(prevPayload => ({ ...prevPayload, [name]: value }));
  }

  const getNurses = async () => {
    try {
      let res = await get(
        `/patients/Allnurse/${sessionStorage.getItem("clinicId")}?clinicId=${sessionStorage.getItem(
          "clinicId"
        )}&pageIndex=1&Pagesize=10`
      );
      setNurses(Array.isArray(res?.data) ? res?.data : []);
    } catch (error) {
      console.error('Error fetching nurses:', error);
    }
  };

  const getDoctors = async () => {
    try {
      let res = await get(
        `/patients/AllDoctor/${sessionStorage.getItem("clinicId")}?clinicId=${sessionStorage.getItem(
          "clinicId"
        )}&pageIndex=1&Pagesize=30`
      );
      setDoctors(Array.isArray(res?.data) ? res?.data : []);
    } catch (error) {
      console.error('Error fetching doctors:', error);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    
    const dateObject = new Date(timestamp);
    
    // Check if the date is valid
    if (isNaN(dateObject.getTime())) return 'Invalid Date';
    
    const formattedDate = dateObject.toISOString().split("T")[0];
    return formattedDate;
  };

  const downloadFile = async (docName) => {
    try {
      const token = sessionStorage.getItem('token');

      if (!token) {
        console.error('Token not found in session storage');
        return;
      }

      const url = `${process.env.REACT_APP_BASE_URL}/labapi/api/document/download-document/${docName}`;

      const options = {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      };

      const response = await fetch(url, options);

      if (response.ok) {
        const blob = await response.blob();

        const blobUrl = URL.createObjectURL(blob);

        const anchor = document.createElement('a');
        anchor.href = blobUrl;
        anchor.download = docName;
        anchor.click();

        URL.revokeObjectURL(blobUrl);
      } else {
        console.error('Failed to fetch download link:', response.statusText);
      }
    } catch (e) {
      console.error('Error fetching download link:', e);
    }
  };

  return (
    <StyledDialog open={true} onClose={closeModal} fullWidth maxWidth="xl">
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Vitals Details</Typography>
          <IconButton onClick={closeModal} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <TableContainer component={Paper} elevation={2} sx={{ mb: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <StyledTableCell>Date</StyledTableCell>
                <StyledTableCell>Blood Pressure (mmHg)</StyledTableCell>
                <StyledTableCell>Temp (°C)</StyledTableCell>
                <StyledTableCell>Weight (Kg)</StyledTableCell>
                <StyledTableCell>Height (cm)</StyledTableCell>
                <StyledTableCell>BMI</StyledTableCell>
                <StyledTableCell>Heart (bpm)</StyledTableCell>
                <StyledTableCell>Oxygen Saturation (SpO₂)</StyledTableCell>
                <StyledTableCell>Blood sugar</StyledTableCell>
                <StyledTableCell>Resp</StyledTableCell>
                <StyledTableCell>Admin Nurse</StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <StyledTableCell>{formatDate(visit?.dateOfVisit)}</StyledTableCell>
                <StyledTableCell>{visit?.bloodPressure}</StyledTableCell>
                <StyledTableCell>{visit?.temperature}</StyledTableCell>
                <StyledTableCell>{visit?.weight}</StyledTableCell>
                <StyledTableCell>{visit?.height}</StyledTableCell>
                <StyledTableCell>{visit?.bmi}</StyledTableCell>
                <StyledTableCell>{visit?.heartPulse}</StyledTableCell>
                <StyledTableCell>{visit?.oxygenSaturation}</StyledTableCell>
                <StyledTableCell>{visit?.bloodSugar}</StyledTableCell>
                <StyledTableCell>{visit?.respiratory}</StyledTableCell>
                <StyledTableCell>{visit?.vitalNurseName}</StyledTableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Assigned Nurse"
              value={visit?.vitalNurseName || ''}
              InputProps={{ readOnly: true }}
            />
          </Grid>

          {visit?.notes && visit.notes.map((note, index) => (
            <Grid item xs={12} key={index}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Additional Notes"
                value={note}
                InputProps={{ readOnly: true }}
              />
            </Grid>
          ))}
        </Grid>

        {visit?.vitalDocuments?.filter(doc => doc?.docName && doc?.docPath).map((doc, index) => (
          <Box key={index} sx={{ mt: 3 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <IconButton size="small" onClick={() => downloadFile(doc?.docName)}>
                <DownloadIcon color="primary" />
              </IconButton>
              <Link
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  downloadFile(doc?.docName);
                }}
                sx={{ color: '#17621A' }}
              >
                {doc?.docName}
              </Link>
            </Stack>

            <Box sx={{ mt: 1, width: '100%' }}>
              {doc?.docPath?.endsWith('.pdf') || doc?.docPath?.endsWith('.tif') || 
               doc?.docPath?.endsWith('.bmp') || doc?.docPath?.endsWith('.tiff') ? (
                <iframe
                  src={`https://docs.google.com/gview?url=${encodeURIComponent(doc?.docPath)}&embedded=true`}
                  title={doc?.docName}
                  style={{ width: '100%', height: '500px', border: 'none' }}
                />
              ) : doc?.docPath?.endsWith('.png') || doc?.docPath?.endsWith('.jpg') || 
                  doc?.docPath?.endsWith('.jpeg') ? (
                <Box
                  component="img"
                  src={doc?.docPath}
                  alt={doc?.docName}
                  sx={{
                    maxWidth: '100%',
                    maxHeight: '80vh',
                    objectFit: 'contain'
                  }}
                />
              ) : (
                <Link
                  href={doc?.docPath}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ color: '#17621A' }}
                >
                  {doc?.docName}
                </Link>
              )}
            </Box>
          </Box>
        ))}
      </DialogContent>
    </StyledDialog>
  );
}

export default ViewVisit;